import moment from "moment";
import { Pie } from "react-chartjs-2";
import { connect } from "react-redux";
import React, { useState, useEffect } from "react";
import { Box, Grid, Card, CircularProgress } from "@material-ui/core";

import toJs from "../../../hoc/toJS";
import select from "../../../utils/select";

// classify — phân loại công bố theo LOẠI HÌNH (dựa thẳng vào `type`, không
// phụ thuộc dữ liệu tra cứu journal/conference) → mọi công bố đã duyệt đều
// rơi đúng 1 trong 6 nhóm, không có công bố nào bị bỏ sót khỏi biểu đồ. Đây
// là góc nhìn "cơ cấu công bố" bổ sung cho biểu đồ xếp hạng ISI/Scopus.
const TYPE_GROUP = {
  scientificResearchJournal: "JournalInternational",
  scientificResearchJournalDomestic: "JournalDomestic",
  scientificResearchConference: "ConferenceInternational",
  scientificResearchConferenceDomestic: "ConferenceDomestic",
  chapterJournal: "Chapter",
  chapterConference: "Chapter",
  chapterJournalDomestic: "Chapter",
  chapterConferenceDomestic: "Chapter",
  working: "Other",
  scientificResearchOther: "Other",
};

const classify = (scientificResearchElement) => {
  const type = scientificResearchElement?.scientificResearch?.type;
  return TYPE_GROUP[type] || "Other";
};

const countByGroup = (list) => {
  const counts = {
    JournalInternational: 0,
    JournalDomestic: 0,
    ConferenceInternational: 0,
    ConferenceDomestic: 0,
    Chapter: 0,
    Other: 0,
  };
  (list || []).forEach((item) => {
    if (!item?.scientificResearch?.accepted) return;
    counts[classify(item)] += 1;
  });
  return counts;
};

function ScientificResearchTypeStatistic(props) {
  const [counts, setCounts] = useState(countByGroup([]));
  const [mainCounts, setMainCounts] = useState(countByGroup([]));

  const dateRangeLabel = () => {
    if (!props.chosenStartDate && !props.chosenEndDate) return "";
    const from = props.chosenStartDate ? moment(props.chosenStartDate).format("DD/MM/YYYY") : "";
    const to = props.chosenEndDate ? moment(props.chosenEndDate).format("DD/MM/YYYY") : "";
    if (from && to) return `từ ${from} đến ${to}`;
    if (from) return `từ ${from}`;
    return `đến ${to}`;
  };

  const pieOptions = {
    title: { display: true, fontSize: 13, fontColor: "#000000" },
    legend: { position: "left" },
  };

  const pieOptionsMain = {
    title: { display: true, fontSize: 13, fontColor: "#000000" },
    legend: { position: "left" },
  };

  useEffect(() => {
    if (!Array.isArray(props.scientificResearchs)) return;

    setCounts(countByGroup(props.scientificResearchs));

    const mainScientificResearchs = props.scientificResearchs.filter((item) =>
      (item?.scientificResearch?.scientificResearchProfile || []).some((author) => author.roleInPaper === "main")
    );
    setMainCounts(countByGroup(mainScientificResearchs));
  }, [props.scientificResearchs]);

  const {
    JournalInternational,
    JournalDomestic,
    ConferenceInternational,
    ConferenceDomestic,
    Chapter,
    Other,
  } = counts;
  const {
    JournalInternational: mainJournalInternational,
    JournalDomestic: mainJournalDomestic,
    ConferenceInternational: mainConferenceInternational,
    ConferenceDomestic: mainConferenceDomestic,
    Chapter: mainChapter,
    Other: mainOther,
  } = mainCounts;

  const backgroundColor = [
    "#243474", // blue — Tạp chí quốc tế
    "#36A2EB", // light blue — Tạp chí trong nước
    "#f0c808", // yellow — Hội thảo quốc tế
    "#FFCE56", // light yellow — Hội thảo trong nước
    "#32936f", // green — Chương sách
    "#9e9e9e", // gray — Khác
  ];

  const labels = [
    "Tạp chí quốc tế",
    "Tạp chí trong nước",
    "Hội thảo quốc tế",
    "Hội thảo trong nước",
    "Chương sách",
    "Khác",
  ];

  const pieData = {
    labels,
    datasets: [
      {
        data: [JournalInternational, JournalDomestic, ConferenceInternational, ConferenceDomestic, Chapter, Other],
        backgroundColor,
      },
    ],
  };

  const pieDataMain = {
    labels,
    datasets: [
      {
        data: [
          mainJournalInternational,
          mainJournalDomestic,
          mainConferenceInternational,
          mainConferenceDomestic,
          mainChapter,
          mainOther,
        ],
        backgroundColor,
      },
    ],
  };

  const total = JournalInternational + JournalDomestic + ConferenceInternational + ConferenceDomestic + Chapter + Other;
  const mainTotal =
    mainJournalInternational +
    mainJournalDomestic +
    mainConferenceInternational +
    mainConferenceDomestic +
    mainChapter +
    mainOther;

  return (
    <Grid container style={{ height: "100%" }}>
      <Grid item xs={6} style={{ height: "100%" }}>
        <Card
          style={{
            height: "100%",
            width: "calc(100% - 20px)",
            padding: "0 8px",
            textAlign: "center",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          {props.isFetching ? (
            <CircularProgress />
          ) : (
            <>
              <Box height={24}>
                <b style={{ fontSize: 13, lineHeight: "24px" }}>
                  {`Phân loại công bố theo loại hình ${dateRangeLabel()}`}
                </b>
              </Box>
              <Box height="calc(100% - 48px)">
                <Pie data={pieData} options={pieOptions} />
              </Box>
              <Box style={{ textAlign: "center", fontSize: 13, lineHeight: "24px" }}>
                Tổng số công bố: {total}
              </Box>
            </>
          )}
        </Card>
      </Grid>
      <Grid item xs={6} style={{ height: "100%" }}>
        <Card
          style={{
            height: "100%",
            marginLeft: 4,
            width: "calc(100% - 20px)",
            padding: "0 8px",
            textAlign: "center",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          {props.isFetching ? (
            <CircularProgress />
          ) : (
            <>
              <Box height={24}>
                <b style={{ fontSize: 13 }}>
                  {`Phân loại công bố theo loại hình có tác giả chính ${dateRangeLabel()}`}
                </b>
              </Box>
              <Box height="calc(100% - 48px)">
                <Pie data={pieDataMain} options={pieOptionsMain} />
              </Box>
              <Box style={{ textAlign: "center", fontSize: 13, letterSpacing: -1, lineHeight: "24px" }}>
                Tổng số công bố có tác giả chính ở CMC: {mainTotal}
              </Box>
            </>
          )}
        </Card>
      </Grid>
    </Grid>
  );
}

const mapStateToProps = (state) => ({
  isFetching: select(state, "homeReducer", "isFetching"),
  scientificResearchs: select(state, "homeReducer", "dataScientificResearch"),
});

const mapDispatchToProps = () => ({});

export default connect(mapStateToProps, mapDispatchToProps)(toJs(ScientificResearchTypeStatistic));
